import { CircularProgress, IconButton, Modal, TableRow } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ProductOrder } from "../../../../../data/entities";
import CustomTable from "../../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../../shared/components/CustomTable/Styled";
import SearchBarWithSuggestion from "../../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import TextBox from "../../../../../shared/components/TextBox";
import { api } from "../../../../../shared/hooks/api";
import useMergeState from "../../../../../shared/hooks/useMergeState";
import DeleteIcon from "../../../../../shared/svg/DeleteIcon";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../../utils/format";
import { error } from "../../../../../utils/toast";
import {
    ButtonRow,
    Expandable,
    ModalWrapper,
    StyledBackdrop,
    StyledModalButton,
    TitleText,
    TitleWrapper,
} from "../../Styled";
import { RequestImportProductForm } from "./RequestImportProductForm";

const RequestImportProductFormTableColumns = [
    { text: "Tên sản phẩm", icon: <></> },
    { text: "Số lượng đề nghị", icon: <></> },
    { text: "Ghi chú", icon: <></> },
    { text: "", icon: <></> },
];

interface ProductRowProps {
    item: any;
    extraRowProps: {
        onChangeImportQuantity: (id: string, quantity: number) => void;
        onChangeNote: (id: string, note: string) => void;
        removeSelectedItem: (item: any) => void;
    };
}

interface ValidationResult {
    isValid: boolean;
    errors: string[];
}

const ProductRow = ({ item, extraRowProps }: ProductRowProps) => {
    const { onChangeImportQuantity, onChangeNote, removeSelectedItem } = extraRowProps;

    return (
        <TableRow>
            <StyledTableCell>{item.name}</StyledTableCell>
            <StyledTableCell>
                <TextBox
                    numeric
                    style={{ width: "80px" }}
                    text={formatInternationalNumber(item.importQuantity ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        extraRowProps.onChangeImportQuantity(item.id, n);
                    }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <TextBox
                    text={item.note || ""}
                    onChangeText={(value: string) => onChangeNote(item.id, value)}
                    style={{ width: "150px" }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <IconButton onClick={() => removeSelectedItem(item)}>
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    );
};

const initialData = {
    receiver_name: "",
    customer: null,
    import_method: "",
    note: "",
    products: [],
    import_date: new Date(),
};

type PrefillItem = {
    id: string;
    name: string;
    importQuantity: number;
    note?: string;
};

interface RequestImportProductModalProps {
    open: boolean;
    handleClose: () => void;
    onUpdate: (importData: any) => void | Promise<void>;
    onImport: (importData: any) => void | Promise<void>;
    saleOrder: ProductOrder;
    initialFormData?: Partial<typeof initialData>;
    initialItems?: PrefillItem[];
    isEditing?: boolean;
    isReserve?: boolean;
}

export const RequestImportProductModal = ({
                                              open,
                                              handleClose,
                                              onImport,
                                              initialFormData,
                                              initialItems,
                                              saleOrder,
                                              isEditing,
                                              onUpdate,
                                              isReserve = false,
                                          }: RequestImportProductModalProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [tableItems, setTableItems] = useState<any[]>([]);
    const [selectedItems, setSelectedItems] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, mergeFormData] = useMergeState(initialData);
    const [{ data, variables }, fetchProducts] = api.get(
        `/products?is_active=true&page_size=${Number.MAX_SAFE_INTEGER}`,
        { page_size: Number.MAX_SAFE_INTEGER }, // Explicitly set in variables
        true,
    );

    const filteredProducts =
        data &&
        data.results.filter((product: any) =>
            saleOrder.products.some((p: any) => p.id === product.id) &&
            !tableItems.some((item: any) => item.id === product.id),
        );

    const fetchItems = (variables: any) => {
        fetchProducts({
            ...variables,
            page_size: Number.MAX_SAFE_INTEGER,
        });
    };

    useEffect(() => {
        if (open) {
            setIsOpen(true);
            if (initialFormData) {
                mergeFormData({ ...initialData, ...initialFormData });
            } else {
                mergeFormData({ ...initialData });
            }
            if (initialItems && Array.isArray(initialItems) && initialItems.length > 0) {
                setTableItems(initialItems);
                setSelectedItems(initialItems);
            } else if (!isEditing && saleOrder.products && saleOrder.products.length > 0) {
                const autoPopulatedProducts = saleOrder.products
                    .map((product: any) => {
                        const reserveProgress =
                            saleOrder.reserve_import_progress?.products?.find(
                                (item: any) =>
                                    item.product_id === product.id,
                            );
                        const requiredQuantity =
                            product.selectedQuantity || 0;
                        const currentStock = product.quantity || 0;
                        const missingQuantity = isReserve
                            ? Number(
                                  reserveProgress?.remaining_qty ??
                                      requiredQuantity,
                              )
                            : Math.max(requiredQuantity - currentStock, 0);

                        return {
                            id: product.id,
                            name: product.name,
                            importQuantity: missingQuantity,
                            note: "",
                        };
                    })
                    .filter(
                        (product: any) =>
                            !isReserve || product.importQuantity > 0,
                    );
                setTableItems(autoPopulatedProducts);
                setSelectedItems(autoPopulatedProducts);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleCloseWithAnimation = () => {
        if (isSubmitting) return;
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    const handleOnclickItem = (item: any, checked: boolean) => {
        if (checked) {
            if (!tableItems.find((product) => product.id === item.id)) {
                setTableItems([
                    ...tableItems,
                    {
                        ...item,
                        importQuantity: 0,
                        import_date: new Date().toISOString().split("T")[0],
                        note: "",
                    },
                ]);
                setSelectedItems([...selectedItems, item]);
            }
        } else {
            const updatedItems = tableItems.filter((product) => product.id !== item.id);
            const updatedSelectedItems = selectedItems.filter((product) => product.id !== item.id);
            setTableItems(updatedItems);
            setSelectedItems(updatedSelectedItems);
        }
    };

    const onChangeImportQuantity = (id: string, quantity: number) => {
        const updatedProducts = tableItems.map((product) => {
            if (product.id === id) return { ...product, importQuantity: quantity };
            return product;
        });
        setTableItems(updatedProducts);
    };

    const onChangeNote = (id: string, note: string) => {
        const updatedProducts = tableItems.map((product) => {
            if (product.id === id) return { ...product, note };
            return product;
        });
        setTableItems(updatedProducts);
    };

    const removeSelectedItem = (removedItem: any) => {
        const updatedItems = tableItems.filter((item) => item.id !== removedItem.id);
        const updatedSelectedItems = selectedItems.filter((item) => item.id !== removedItem.id);
        setTableItems(updatedItems);
        setSelectedItems(updatedSelectedItems);
    };

    const onClickRequestImport = async () => {
        if (isSubmitting) return;

        const validation = validateImportData();
        if (!validation.isValid) {
            alert(validation.errors.join("\n\n"));
            return;
        }

        const formattedProducts = tableItems.map((item) => ({
            product: item.id,
            required_quantity: item.importQuantity,
            quantity: item.importQuantity,
            note: item.note || "",
        }));

        const importData = {
            ...formData,
            products: formattedProducts,
            sale_order: saleOrder.id,
        };

        try {
            setIsSubmitting(true);
            if (isEditing) {
                await Promise.resolve(onUpdate(importData));
            } else {
                await Promise.resolve(onImport(importData));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateImportData = (): ValidationResult => {
        const errors: string[] = [];

        // Validate products
        if (tableItems.length === 0) {
            errors.push("Vui lòng chọn ít nhất một sản phẩm");
        }

        // Check if any products have zero or negative quantity
        const invalidQuantityProducts = tableItems.filter(
            (item) => !item.importQuantity || item.importQuantity <= 0,
        );

        if (invalidQuantityProducts.length > 0) {
            errors.push("Vui lòng nhập số lượng lớn hơn 0 cho tất cả sản phẩm");
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    };

    return (
        <>
            <Modal
                open={open}
                onClose={(_, reason) => {
                    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                    handleCloseWithAnimation();
                }}
                closeAfterTransition
                slots={{
                    backdrop: StyledBackdrop,
                }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                        onClick: (e: React.MouseEvent) => e.stopPropagation(),
                    },
                }}
            >
                <ModalWrapper isOpen={isOpen}>
                    <Expandable>
                        <TitleWrapper>
                            <TitleText>Đề nghị nhập kho sản phẩm</TitleText>
                        </TitleWrapper>

                        <RequestImportProductForm
                            formData={formData}
                            onChange={mergeFormData}
                            isReserve={isReserve}
                        />

                        <TitleText>Danh sách sản phẩm</TitleText>

                        {!isEditing && (
                            <SearchBarWithSuggestion
                                menuList={filteredProducts ? filteredProducts : []}
                                selectedItems={selectedItems}
                                fetchItems={fetchItems}
                                handleOnclickItem={handleOnclickItem}
                            />
                        )}

                        <CustomTable
                            itemList={tableItems}
                            RowElement={ProductRow}
                            TableColumns={RequestImportProductFormTableColumns}
                            extraRowProps={{
                                onChangeImportQuantity,
                                onChangeNote,
                                removeSelectedItem,
                            }}
                            containerStyle={{ maxHeight: "400px" }}
                        />
                    </Expandable>

                    <ButtonRow>
                        <StyledModalButton variant="outlined" onClick={handleCloseWithAnimation} disabled={isSubmitting}>
                            Huỷ bỏ
                        </StyledModalButton>
                        <StyledModalButton onClick={onClickRequestImport} variant="contained" disabled={isSubmitting}>
                            {isSubmitting && <CircularProgress size={16} color="inherit" />}
                            {isEditing ? "Cập nhật đề nghị" : "Đề nghị nhập kho"}
                        </StyledModalButton>
                    </ButtonRow>
                </ModalWrapper>
            </Modal>
        </>
    );
};
